/**
 * MURAL MAP — mural-map.js
 * Upload to: Shopify Admin → Online Store → Themes → Assets
 */

(function () {
  'use strict';

  /* ── Map style presets ───────────────────────────────────────────── */
  const MAP_STYLES = {
    default: [],
    dark: [
      { elementType: 'geometry', stylers: [{ color: '#212121' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
    ],
    minimal: [
      { elementType: 'geometry', stylers: [{ color: '#f4f1ea' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e0dbd0' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#cac6b8' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    ],
    artistic: [
      { elementType: 'geometry', stylers: [{ color: '#0a0010' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#8a6aaa' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0010' }] },
      { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#1c0038' }] },
      { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#3a0070' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1c0038' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2a0050' }] },
      { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#3a0070' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#050008' }] },
      { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3a0070' }] },
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#0e0020' }] },
    ]
  };

  /* ── State ───────────────────────────────────────────────────────── */
  let map = null;
  let activePin = null;
  let murals = [];

  /* ── DOM refs ────────────────────────────────────────────────────── */
  const modal         = document.getElementById('mural-modal');
  const modalBackdrop = modal?.querySelector('.mural-modal-backdrop');
  const modalClose    = modal?.querySelector('.mural-modal-close');
  const modalImage    = document.getElementById('modal-mural-image');
  const modalLocation = document.getElementById('modal-mural-location');
  const modalName     = document.getElementById('modal-mural-name');
  const modalDesc     = document.getElementById('modal-mural-description');
  const productsWrap  = document.getElementById('modal-mural-products');
  const productsGrid  = document.getElementById('modal-products-grid');

  /* ── Load mural JSON ─────────────────────────────────────────────── */
  function loadMuralData() {
    const el = document.getElementById('mural-data');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[MuralMap] Could not parse mural data:', e);
      return [];
    }
  }

  /* ── Group murals by location (rounded to 4 decimal places) ──────── */
  function groupByLocation(murals) {
    const groups = {};
    murals.forEach(mural => {
      if (!mural.lat && !mural.lng) return;
      const key = `${parseFloat(mural.lat).toFixed(4)},${parseFloat(mural.lng).toFixed(4)}`;
      if (!groups[key]) {
        groups[key] = { lat: mural.lat, lng: mural.lng, murals: [] };
      }
      groups[key].murals.push(mural);
    });
    return Object.values(groups);
  }

  /* ── Initialise Google Maps ──────────────────────────────────────── */
  function initMap() {
    murals = loadMuralData();
    const canvas = document.getElementById('mural-map-canvas');
    if (!canvas) return;

    const styleKey = window.MURAL_MAP_STYLE || 'artistic';
    const styles   = MAP_STYLES[styleKey] || MAP_STYLES.artistic;

    map = new google.maps.Map(canvas, {
      center: { lat: 40.7957, lng: -73.4096 },
      zoom: 10,
      styles,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      gestureHandling: 'greedy',
    });

    const groups = groupByLocation(murals);
    groups.forEach(group => addPin(group));
    setupModalEvents();
    initGallery();
    initCityFunding();
  }

  /* ── Cloud logo pin ─────────────────────────────────────────────── */
  const LOGO_URL = 'https://cdn.shopify.com/s/files/1/2644/4912/files/Head_in_the_clouds_logo_83e8f5bc-6116-4918-8a49-8fbb3f1b912b.png?v=1783309841';

  function isGone(mural) {
    return mural.name && mural.name.toLowerCase().includes('(gone)');
  }

  function logoPin(isCluster, count, gone) {
    const badgeHTML = isCluster ? `<div class="mural-pin-badge">${count}</div>` : '';
    const goneHTML  = gone ? `<div class="mural-pin-gone-label">Gone</div>` : '';
    const cls = gone ? 'mural-pin-logo mural-pin-logo--gone' : 'mural-pin-logo';
    return `
      <img src="${LOGO_URL}" alt="mural location" class="${cls}" draggable="false"/>
      ${goneHTML}
      ${badgeHTML}
    `;
  }

  /* ── Add a pin (single or cluster) ──────────────────────────────── */
  function addPin(group) {
    const isCluster = group.murals.length > 1;

    const pinEl = document.createElement('div');
    pinEl.className = isCluster ? 'mural-pin mural-pin--cluster' : 'mural-pin';
    pinEl.setAttribute('role', 'button');
    pinEl.setAttribute('tabindex', '0');
    const groupGone = !isCluster && isGone(group.murals[0]);
    if (groupGone) pinEl.classList.add('mural-pin--gone');
    pinEl.innerHTML = logoPin(isCluster, group.murals.length, groupGone);

    if (isCluster) {
      pinEl.setAttribute('aria-label', `${group.murals.length} murals at this location`);
    } else {
      pinEl.setAttribute('aria-label', `View mural: ${group.murals[0].name}`);
      pinEl.setAttribute('title', group.murals[0].name);
    }

    const overlay = new google.maps.OverlayView();

    overlay.onAdd = function () {
      this.getPanes().overlayMouseTarget.appendChild(pinEl);
    };

    overlay.draw = function () {
      const proj  = this.getProjection();
      const point = proj.fromLatLngToDivPixel(
        new google.maps.LatLng(group.lat, group.lng)
      );
      if (!point) return;
      pinEl.style.left     = point.x + 'px';
      pinEl.style.top      = point.y + 'px';
      pinEl.style.position = 'absolute';
    };

    overlay.onRemove = function () {
      pinEl.parentNode?.removeChild(pinEl);
    };

    overlay.setMap(map);

    function handleOpen(e) {
      e?.stopPropagation();
      if (activePin) activePin.classList.remove('active');
      pinEl.classList.add('active');
      activePin = pinEl;

      if (isCluster) {
        openPicker(group.murals, pinEl);
      } else {
        openModal(group.murals[0]);
      }
    }

    pinEl.addEventListener('click', handleOpen);
    pinEl.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') handleOpen(e);
    });
  }

  /* ── Picker menu for clustered pins ─────────────────────────────── */
  function openPicker(muralsAtLocation, pinEl) {
    closePicker();

    const picker = document.createElement('div');
    picker.id = 'mural-picker';
    picker.className = 'mural-picker';
    picker.setAttribute('role', 'menu');

    const heading = document.createElement('p');
    heading.className = 'mural-picker-heading';
    heading.textContent = `${muralsAtLocation.length} Murals at this location`;
    picker.appendChild(heading);

    muralsAtLocation.forEach(mural => {
      const btn = document.createElement('button');
      btn.className = 'mural-picker-item';
      btn.setAttribute('role', 'menuitem');

      const thumb = document.createElement('div');
      thumb.className = 'mural-picker-thumb';
      if (mural.image) {
        const img = document.createElement('img');
        img.src = mural.image;
        img.alt = mural.name;
        thumb.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'mural-picker-info';

      const name = document.createElement('span');
      name.className = 'mural-picker-name';
      name.textContent = mural.name ? mural.name.replace(/\(gone\)/gi, '').trim() : 'Untitled';

      const year = document.createElement('span');
      year.className = 'mural-picker-year';
      year.textContent = mural.year || '';

      info.appendChild(name);
      if (mural.year) info.appendChild(year);

      btn.appendChild(thumb);
      btn.appendChild(info);

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        closePicker();
        openModal(mural);
      });

      picker.appendChild(btn);
    });

    // Append to body so it's never affected by pin transforms
    document.body.appendChild(picker);

    // Position above the pin using screen coordinates
    const pinRect = pinEl.getBoundingClientRect();
    const pickerWidth = 240;
    const margin = 8;

    let left = pinRect.left + (pinRect.width / 2) - (pickerWidth / 2);
    let top  = pinRect.top - picker.offsetHeight - 12;

    // Keep within viewport horizontally
    left = Math.max(margin, Math.min(left, window.innerWidth - pickerWidth - margin));

    // If not enough room above, show below
    if (top < margin) {
      top = pinRect.bottom + 12;
    }

    picker.style.left  = left + 'px';
    picker.style.top   = top  + 'px';
    picker.style.width = pickerWidth + 'px';

    // Recalculate after render for accurate height
    requestAnimationFrame(() => {
      let t = pinRect.top - picker.offsetHeight - 12;
      if (t < margin) t = pinRect.bottom + 12;
      t = Math.max(margin, Math.min(t, window.innerHeight - picker.offsetHeight - margin));
      picker.style.top = t + 'px';
    });

    // Close when clicking outside
    setTimeout(() => {
      document.addEventListener('click', closePicker, { once: true });
    }, 0);
  }

  function closePicker() {
    const existing = document.getElementById('mural-picker');
    if (existing) existing.remove();
  }

  /* ── Modal: open ─────────────────────────────────────────────────── */
  function openModal(mural) {
    if (!modal) return;
    closePicker();

    // Build images array
    const images = (mural.images || [mural.image]).filter(Boolean);
    let currentImg = 0;

    function showImage(idx) {
      currentImg = idx;
      const src = images[idx];
      if (src) {
        modalImage.src = src;
        modalImage.alt = mural.name;
        modalImage.style.display = 'block';
        modalImage.style.cursor = 'zoom-in';
        modalImage.onclick = () => openLightbox(src, mural.name);
      } else {
        modalImage.style.display = 'none';
        modalImage.onclick = null;
      }
      const counter = document.getElementById('modal-img-counter');
      if (counter) {
        counter.textContent = images.length > 1 ? `${idx + 1} / ${images.length}` : '';
        counter.style.display = images.length > 1 ? 'block' : 'none';
      }
      document.querySelectorAll('.modal-thumb').forEach((t, i) => {
        t.classList.toggle('active', i === idx);
      });
    }

    // Thumbnail strip
    const thumbStrip = document.getElementById('modal-thumb-strip');
    if (thumbStrip) {
      thumbStrip.innerHTML = '';
      const hasMultiple = images.length > 1 || mural.video_url;
      thumbStrip.style.display = hasMultiple ? 'flex' : 'none';

      images.forEach((src, i) => {
        const th = document.createElement('div');
        th.className = 'modal-thumb' + (i === 0 ? ' active' : '');
        if (src) {
          const img = document.createElement('img');
          img.src = src;
          img.alt = 'Photo ' + (i + 1);
          img.loading = 'lazy';
          th.appendChild(img);
        }
        th.addEventListener('click', () => showImage(i));
        thumbStrip.appendChild(th);
      });

      if (mural.video_url) {
        const vth = document.createElement('div');
        vth.className = 'modal-thumb modal-thumb--video';
        vth.innerHTML = '<div class="modal-thumb-play"></div>';
        vth.addEventListener('click', () => window.open(mural.video_url, '_blank'));
        thumbStrip.appendChild(vth);
      }
    }

    showImage(0);

    // Gone status
    const muralIsGone = isGone(mural);
    const cleanName = mural.name ? mural.name.replace(/\(gone\)/gi, '').trim() : '';

    // Gone banner
    const existingGone = document.getElementById('modal-gone-banner');
    if (existingGone) existingGone.remove();
    if (muralIsGone) {
      const goneBanner = document.createElement('div');
      goneBanner.id = 'modal-gone-banner';
      goneBanner.className = 'mural-gone-banner';
      goneBanner.innerHTML = '<span>This mural no longer exists — it has been painted over or removed.</span>';
      modalName.parentNode.insertBefore(goneBanner, modalName);
    }

    // Location text
    modalLocation.textContent = [mural.city, mural.country, mural.year].filter(Boolean).join(' · ');

    // Directions button
    const existingBtn = document.getElementById('modal-directions-btn');
    if (existingBtn) existingBtn.remove();
    const directionsBtn = document.createElement('a');
    directionsBtn.id = 'modal-directions-btn';
    directionsBtn.href = 'https://www.google.com/maps/dir/?api=1&destination=' + mural.lat + ',' + mural.lng;
    directionsBtn.target = '_blank';
    directionsBtn.rel = 'noopener noreferrer';
    directionsBtn.className = 'mural-directions-btn';
    directionsBtn.textContent = '📍 Get Directions';
    modalLocation.insertAdjacentElement('afterend', directionsBtn);

    // Video card
    const existingVideo = document.getElementById('modal-video-card');
    if (existingVideo) existingVideo.remove();
    if (mural.video_url) {
      const vsrc = mural.video_url;
      const vsource = vsrc.includes('youtube') || vsrc.includes('youtu.be') ? 'YouTube'
        : vsrc.includes('instagram') ? 'Instagram'
        : vsrc.includes('tiktok') ? 'TikTok' : 'Video';
      const videoCard = document.createElement('a');
      videoCard.id = 'modal-video-card';
      videoCard.className = 'modal-video-card';
      videoCard.href = vsrc;
      videoCard.target = '_blank';
      videoCard.rel = 'noopener noreferrer';
      videoCard.innerHTML =
        '<div class="modal-video-play"></div>' +
        '<div class="modal-video-info">' +
          '<span class="modal-video-label">Watch the process</span>' +
          '<span class="modal-video-title">' + (mural.video_title || 'Mural video') + '</span>' +
          '<span class="modal-video-source">' + vsource + ' · Andaluz The Artist</span>' +
        '</div>';
      directionsBtn.insertAdjacentElement('afterend', videoCard);
    }

    // Text
    modalName.textContent = muralIsGone ? (cleanName || 'Untitled') : (mural.name || 'Untitled');
    modalDesc.textContent = mural.description || '';

    // Products
    productsGrid.innerHTML = '';
    if (mural.products && mural.products.length > 0) {
      productsWrap.style.display = 'block';
      mural.products.forEach(product => {
        productsGrid.appendChild(buildProductCard(product));
      });
    } else {
      productsWrap.style.display = 'block';
      const empty = document.createElement('p');
      empty.className = 'mural-no-products';
      empty.textContent = 'No merchandise linked to this mural yet.';
      productsGrid.appendChild(empty);
    }

    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    modalClose.focus();
  }

  /* ── Modal: close ────────────────────────────────────────────────── */
  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
    if (activePin) {
      activePin.classList.remove('active');
      activePin = null;
    }
  }

  /* ── Modal: events ───────────────────────────────────────────────── */
  function setupModalEvents() {
    modalClose?.addEventListener('click', closeModal);
    modalBackdrop?.addEventListener('click', closeModal);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    modal?.addEventListener('keydown', e => {
      if (e.key !== 'Tab') return;
      const focusable = [...modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )].filter(el => !el.hidden && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    });
  }

  /* ── Lightbox ────────────────────────────────────────────────────── */
  function openLightbox(src, alt) {
    const existing = document.getElementById('mural-lightbox');
    if (existing) existing.remove();

    const lb = document.createElement('div');
    lb.id = 'mural-lightbox';
    lb.className = 'mural-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-label', 'Full image view');

    const close = document.createElement('button');
    close.className = 'mural-lightbox-close';
    close.setAttribute('aria-label', 'Close full image');
    close.textContent = '\u00d7';

    const img = document.createElement('img');
    img.src = src;
    img.alt = alt || '';
    img.className = 'mural-lightbox-img';

    const hint = document.createElement('p');
    hint.className = 'mural-lightbox-hint';
    hint.textContent = 'Tap anywhere to close';

    lb.appendChild(close);
    lb.appendChild(img);
    lb.appendChild(hint);
    document.body.appendChild(lb);

    function closeLightbox() {
      lb.remove();
    }

    close.addEventListener('click', (e) => {
      e.stopPropagation();
      closeLightbox();
    });

    lb.addEventListener('click', (e) => {
      if (e.target !== img) closeLightbox();
    });

    document.addEventListener('keydown', function escLb(e) {
      if (e.key === 'Escape') {
        closeLightbox();
        document.removeEventListener('keydown', escLb);
      }
    });
  }

    /* ── Build a product card ────────────────────────────────────────── */
  function buildProductCard(product) {
    const a = document.createElement('a');
    a.className = 'mural-product-card';
    a.href      = product.url || '#';
    a.target    = '_self';

    if (product.image) {
      const img = document.createElement('img');
      img.src     = product.image;
      img.alt     = product.title;
      img.loading = 'lazy';
      a.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'mural-product-card-info';

    const title = document.createElement('p');
    title.className   = 'mural-product-title';
    title.textContent = product.title;
    info.appendChild(title);

    if (product.available === false) {
      const sold = document.createElement('p');
      sold.className   = 'mural-product-sold-out';
      sold.textContent = 'Sold Out';
      info.appendChild(sold);
    } else {
      const price = document.createElement('p');
      price.className   = 'mural-product-price';
      price.textContent = product.price;
      info.appendChild(price);
    }

    a.appendChild(info);
    return a;
  }

  /* ── Gallery ────────────────────────────────────────────────────── */
  function initGallery() {
    const grid    = document.getElementById('mural-gallery-grid');
    const filters = document.getElementById('mural-gallery-filters');
    if (!grid) return;

    // Click on gallery card opens modal with matching mural data
    grid.addEventListener('click', (e) => {
      const card = e.target.closest('.mural-gallery-card');
      if (!card) return;
      const id = parseInt(card.dataset.muralId, 10);
      const mural = murals.find(m => m.id === id);
      if (mural) openModal(mural);
    });

    grid.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const card = e.target.closest('.mural-gallery-card');
      if (!card) return;
      const id = parseInt(card.dataset.muralId, 10);
      const mural = murals.find(m => m.id === id);
      if (mural) openModal(mural);
    });

    // Filter buttons
    if (filters) {
      filters.addEventListener('click', (e) => {
        const btn = e.target.closest('.mural-filter-btn');
        if (!btn) return;
        filters.querySelectorAll('.mural-filter-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const filter = btn.dataset.filter;
        grid.querySelectorAll('.mural-gallery-card').forEach(card => {
          const gone = card.dataset.gone === 'true';
          if (filter === 'all') {
            card.classList.remove('hidden');
          } else if (filter === 'active') {
            card.classList.toggle('hidden', gone);
          } else if (filter === 'gone') {
            card.classList.toggle('hidden', !gone);
          }
        });
      });
    }
  }


  /* ── City Funding ────────────────────────────────────────────────── */
  const CF_GOAL        = window.CF_GOAL        || 10000;
  const CF_PAYMENT     = window.CF_PAYMENT     || '#';
  const CF_STRIPE_LINKS = window.CF_STRIPE_LINKS || {};
  const CF_AMT_OPTIONS = [5,10,25,50,100,250,500,1000,5000,10000];
  const CF_AMT_LABELS  = ['$5','$10','$25','$50','$100','$250','$500','$1k','$5k','$10k'];

  let cfCities      = [];
  let cfSelectedAmt = 50;
  let cfSelectedCity = null;

  function cfLoadCities() {
    const el = document.getElementById('cf-city-data');
    if (!el) return [];
    try { return JSON.parse(el.textContent); } catch(e) { return []; }
  }

  function cfPct(city) {
    return Math.min((city.raised / CF_GOAL) * 100, 100);
  }

  function cfFmtAmt(n) {
    return n >= 1000 ? '$' + (n/1000) + 'k' : '$' + n;
  }

  // Tab switcher
  window.cfTab = function(id, btn) {
    document.querySelectorAll('.cf-tab-panel').forEach(p => p.classList.remove('active'));
    document.querySelectorAll('.cf-tab-btn').forEach(t => t.classList.remove('active'));
    const panel = document.getElementById('cf-tab-' + id);
    if (panel) panel.classList.add('active');
    if (btn) btn.classList.add('active');
  };

  // Build green city funding clouds on the map
  function cfBuildMapClouds() {
    const wrap = document.getElementById('mural-map-canvas');
    if (!wrap) return;
    const overlay = new google.maps.OverlayView();
    overlay.onAdd = function() {
      cfCities.forEach(city => cfAddCityCloud(city));
    };
    overlay.draw = function(){};
    overlay.setMap(map);
  }

  function cfAddCityCloud(city) {
    const p    = cfPct(city);
    // Always large — base 72px, grows to 96px when funded
    const size = city.funded ? 96 : p >= 75 ? 88 : p >= 50 ? 80 : p >= 25 ? 76 : 72;

    const pinEl = document.createElement('div');
    pinEl.className = 'cf-city-cloud' + (city.funded ? ' cf-city-cloud--funded' : '');
    pinEl.setAttribute('role', 'button');
    pinEl.setAttribute('tabindex', '0');
    pinEl.setAttribute('aria-label', `${city.name} — $${city.raised.toLocaleString()} raised`);

    // Build a tinted green version of the logo using canvas
    const canvasId = 'cf-canvas-' + city.id;

    pinEl.innerHTML = `
      <div class="cf-city-cloud-inner">
        <div class="cf-city-tooltip">
          <div class="cf-tt-name">${city.name}</div>
          <div class="cf-tt-raised">$${city.raised.toLocaleString()} raised</div>
          <div class="cf-tt-pct">${p.toFixed(0)}% to goal · ${city.backers} backers</div>
          <div class="cf-tt-cta">Tap to donate</div>
        </div>
        <div class="cf-city-logo-wrap" style="width:${size}px;height:${size}px">
          <div class="cf-city-fill-bg">
            <div class="cf-city-fill-color" style="height:${p}%"></div>
          </div>
          <canvas id="${canvasId}" width="${size}" height="${size}" class="cf-city-logo-canvas" style="width:${size}px;height:${size}px"></canvas>
        </div>
        ${city.funded ? `<div class="cf-city-booked-badge">Booked!</div>` : ''}
        <div class="cf-city-label">${city.name.split(',')[0]}</div>
      </div>`;

    const cfOverlay = new google.maps.OverlayView();
    cfOverlay.onAdd = function() {
      this.getPanes().overlayMouseTarget.appendChild(pinEl);
    };
    cfOverlay.draw = function() {
      const proj  = this.getProjection();
      const point = proj.fromLatLngToDivPixel(new google.maps.LatLng(city.lat, city.lng));
      if (!point) return;
      pinEl.style.left     = point.x + 'px';
      pinEl.style.top      = point.y + 'px';
      pinEl.style.position = 'absolute';
    };
    cfOverlay.onRemove = function() {
      pinEl.parentNode?.removeChild(pinEl);
    };
    cfOverlay.setMap(map);

    pinEl.addEventListener('click', (e) => {
      e.stopPropagation();

      // Check if any mural pins are nearby (within 40px on screen)
      const pinRect = pinEl.getBoundingClientRect();
      const pinCx   = pinRect.left + pinRect.width / 2;
      const pinCy   = pinRect.top  + pinRect.height / 2;
      const nearbyMurals = [];

      document.querySelectorAll('.mural-pin').forEach(mp => {
        const mr  = mp.getBoundingClientRect();
        const mcx = mr.left + mr.width / 2;
        const mcy = mr.top  + mr.height / 2;
        const dist = Math.sqrt(Math.pow(pinCx - mcx, 2) + Math.pow(pinCy - mcy, 2));
        if (dist < 50) {
          const id = parseInt(mp.dataset?.muralId || mp.getAttribute('data-mural-id'));
          const m  = murals.find(m => m.id === id);
          if (m) nearbyMurals.push({ mural: m, pinEl: mp });
        }
      });

      if (nearbyMurals.length > 0) {
        // Show overlay menu with both options
        cfShowPeekMenu(pinEl, city, nearbyMurals);
      } else {
        cfTab('donate', document.querySelector('.cf-tab-btn[data-tab="donate"]'));
        const sel = document.getElementById('cf-city-select');
        if (sel) { sel.value = city.id; cfOnCityChange(); }
        const tabsBar = document.querySelector('.cf-tabs-bar');
        if (tabsBar) tabsBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    // Tint logo green using canvas after image loads
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function() {
      const canvas = document.getElementById('cf-canvas-' + city.id);
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const s = canvas.width;
      ctx.drawImage(img, 0, 0, s, s);
      // Apply green tint overlay
      ctx.globalCompositeOperation = 'source-atop';
      ctx.fillStyle = 'rgba(34, 197, 94, 0.55)';
      ctx.fillRect(0, 0, s, s);
      ctx.globalCompositeOperation = 'source-over';
    };
    img.onerror = function() {
      // Fallback: just show the regular logo img
      const canvas = document.getElementById('cf-canvas-' + city.id);
      if (!canvas) return;
      canvas.outerHTML = `<img class="cf-city-logo-img" src="${LOGO_URL}" style="width:${size}px;height:${size}px" alt="${city.name}">`;
    };
    img.src = LOGO_URL;
  }

  // Build leaderboard
  function cfBuildLeaderboard() {
    const list = document.getElementById('cf-lb-list');
    if (!list) return;
    const sorted = [...cfCities].sort((a,b) => b.raised - a.raised);
    list.innerHTML = '';
    sorted.forEach((city, i) => {
      const p         = cfPct(city);
      const rankClass = i===0?'gold':i===1?'silver':i===2?'bronze':'other';
      const barColor  = p>=100?'#22c55e':p>=50?'linear-gradient(90deg,#9613f0,#da73ff)':'linear-gradient(90deg,#6100ad,#9613f0)';
      const row = document.createElement('div');
      row.className = 'cf-lb-row';
      row.innerHTML = `
        <div class="cf-lb-rank ${rankClass}">${i+1}</div>
        <div class="cf-lb-logo-wrap">
          <div class="cf-lb-fill-bg"><div class="cf-lb-fill" style="height:${p}%"></div></div>
          <img class="cf-lb-logo" src="${LOGO_URL}" alt="${city.name}" loading="lazy">
        </div>
        <div class="cf-lb-info">
          <div class="cf-lb-city">${city.name}</div>
          <div class="cf-lb-bar-bg"><div class="cf-lb-bar" style="width:${p}%;background:${barColor}"></div></div>
        </div>
        <div class="cf-lb-right">
          <div class="cf-lb-amount">$${city.raised.toLocaleString()}</div>
          <div class="cf-lb-pct">${p.toFixed(0)}%</div>
        </div>`;
      row.addEventListener('click', () => {
        cfTab('donate', document.querySelector('.cf-tab-btn[data-tab="donate"]'));
        const sel = document.getElementById('cf-city-select');
        if (sel) { sel.value = city.id; cfOnCityChange(); }
      });
      list.appendChild(row);
    });
  }

  // Build city select
  function cfBuildSelect() {
    const sel = document.getElementById('cf-city-select');
    if (!sel) return;
    cfCities.forEach(city => {
      const opt = document.createElement('option');
      opt.value = city.id;
      opt.textContent = city.name;
      sel.appendChild(opt);
    });
  }

  // Build amount buttons
  function cfBuildAmounts() {
    const grid = document.getElementById('cf-amounts-grid');
    if (!grid) return;
    grid.innerHTML = '';
    CF_AMT_OPTIONS.forEach((amt, i) => {
      const btn = document.createElement('div');
      btn.className = 'cf-amt' + (amt === cfSelectedAmt ? ' selected' : '');
      btn.textContent = CF_AMT_LABELS[i];

      // Show Stripe link indicator if configured
      const hasStripe = CF_STRIPE_LINKS[amt];
      if (hasStripe) {
        btn.setAttribute('title', 'Pay with Stripe');
      }

      btn.addEventListener('click', () => {
        cfSelectedAmt = amt;
        const ci = document.getElementById('cf-custom-input');
        if (ci) ci.value = '';
        cfBuildAmounts();
        cfUpdatePerks();
        cfUpdateDonateBtn();
      });
      grid.appendChild(btn);
    });
  }

  // City change handler
  window.cfOnCityChange = function() {
    const sel = document.getElementById('cf-city-select');
    if (!sel) return;
    const city = cfCities.find(c => c.id === sel.value);
    if (!city) { const pr = document.getElementById('cf-progress-row'); if(pr) pr.style.display='none'; return; }
    cfSelectedCity = city;
    const p = cfPct(city);
    const pr = document.getElementById('cf-progress-row'); if(pr) pr.style.display='flex';
    const el = (id) => document.getElementById(id);
    if(el('cf-prog-city'))     el('cf-prog-city').textContent    = city.name;
    if(el('cf-prog-raised'))   el('cf-prog-raised').textContent  = '$' + city.raised.toLocaleString() + ' raised';
    if(el('cf-prog-bar-fill')) el('cf-prog-bar-fill').style.width = p + '%';
    if(el('cf-prog-pct'))      el('cf-prog-pct').textContent     = p.toFixed(0) + '% funded';
    if(el('cf-prog-backers'))  el('cf-prog-backers').textContent = city.backers + ' backers';
    if(el('cf-mini-fill'))     el('cf-mini-fill').style.height   = p + '%';
    const bb = el('cf-booked-banner');
    if(bb) bb.style.display = city.funded || city.raised >= CF_GOAL ? 'block' : 'none';
    cfUpdateDonateBtn();
  };

  // Custom amount
  window.cfOnCustom = function() {
    const ci = document.getElementById('cf-custom-input');
    if (!ci) return;
    const val = parseInt(ci.value);
    const customBtn = document.getElementById('cf-custom-stripe-btn');

    if (val && val >= 5) {
      cfSelectedAmt = Math.min(val, 10000);

      // Deselect preset buttons
      document.querySelectorAll('.cf-amt').forEach(b => b.classList.remove('selected'));
      cfUpdatePerks();
      cfUpdateDonateBtn();

      // Show custom Stripe button if link is configured
      const customLink = CF_STRIPE_LINKS['custom'];
      if (customBtn) {
        if (customLink && cfSelectedCity) {
          // Pass quantity as the dollar amount and city as reference
          const sep = customLink.includes('?') ? '&' : '?';
          const city = cfSelectedCity ? encodeURIComponent(cfSelectedCity.name) : '';
          customBtn.href = `${customLink}${sep}quantity=${cfSelectedAmt}&client_reference_id=${city}`;
          customBtn.textContent = `Pay $${cfSelectedAmt} via Stripe`;
          customBtn.style.display = 'block';
        } else if (customLink && !cfSelectedCity) {
          customBtn.href = customLink;
          customBtn.textContent = `Pay $${cfSelectedAmt} via Stripe`;
          customBtn.style.display = 'block';
        } else {
          customBtn.style.display = 'none';
        }
      }
    } else {
      if (customBtn) customBtn.style.display = 'none';
    }
  };

  // Update perks
  function cfUpdatePerks() {
    [[5,'cf-p5'],[25,'cf-p25'],[100,'cf-p100'],[500,'cf-p500'],[1000,'cf-p1000'],[10000,'cf-p10000']].forEach(([min,id]) => {
      const el = document.getElementById(id);
      if (el) el.classList.toggle('lit', cfSelectedAmt >= min);
    });
  }

  // Update donate button
  function cfUpdateDonateBtn() {
    const btn = document.getElementById('cf-donate-btn');
    if (!btn) return;
    const cityName = cfSelectedCity ? cfSelectedCity.name.split(',')[0] : null;
    if (!cityName) {
      btn.textContent = 'Select a city to donate';
      btn.href = '#';
      btn.style.opacity = '0.5';
      btn.style.pointerEvents = 'none';
      return;
    }

    btn.style.opacity = '1';
    btn.style.pointerEvents = 'all';
    btn.textContent = `Donate ${cfFmtAmt(cfSelectedAmt)} for ${cityName}`;

    // Use specific Stripe link for this amount if available
    const stripeLink = CF_STRIPE_LINKS[cfSelectedAmt];
    if (stripeLink) {
      // Append city as client_reference_id for Stripe tracking
      const sep = stripeLink.includes('?') ? '&' : '?';
      btn.href = `${stripeLink}${sep}client_reference_id=${encodeURIComponent(cfSelectedCity.name)}&prefilled_email=`;
    } else if (CF_PAYMENT && CF_PAYMENT !== '#') {
      // Fallback to general payment link
      const sep = CF_PAYMENT.includes('?') ? '&' : '?';
      btn.href = `${CF_PAYMENT}${sep}city=${encodeURIComponent(cfSelectedCity.name)}&amount=${cfSelectedAmt}`;
    } else {
      btn.href = '#';
    }
  }

  // Init city funding
  function initCityFunding() {
    cfCities = cfLoadCities();
    if (!cfCities.length) return;

    // Add lat/lng from predefined city coords
    const cityCoords = {
      'New York City, NY':   { lat: 40.7128, lng: -74.0060 },
      'Los Angeles, CA':     { lat: 34.0522, lng: -118.2437 },
      'Chicago, IL':         { lat: 41.8781, lng: -87.6298 },
      'Houston, TX':         { lat: 29.7604, lng: -95.3698 },
      'Phoenix, AZ':         { lat: 33.4484, lng: -112.0740 },
      'Philadelphia, PA':    { lat: 39.9526, lng: -75.1652 },
      'San Antonio, TX':     { lat: 29.4241, lng: -98.4936 },
      'San Diego, CA':       { lat: 32.7157, lng: -117.1611 },
      'Dallas, TX':          { lat: 32.7767, lng: -96.7970 },
      'San Jose, CA':        { lat: 37.3382, lng: -121.8863 },
      'Austin, TX':          { lat: 30.2672, lng: -97.7431 },
      'Jacksonville, FL':    { lat: 30.3322, lng: -81.6557 },
      'San Francisco, CA':   { lat: 37.7749, lng: -122.4194 },
      'Columbus, OH':        { lat: 39.9612, lng: -82.9988 },
      'Indianapolis, IN':    { lat: 39.7684, lng: -86.1581 },
      'Seattle, WA':         { lat: 47.6062, lng: -122.3321 },
      'Denver, CO':          { lat: 39.7392, lng: -104.9903 },
      'Miami, FL':           { lat: 25.7617, lng: -80.1918 },
      'Nashville, TN':       { lat: 36.1627, lng: -86.7816 },
      'Portland, OR':        { lat: 45.5051, lng: -122.6750 },
    };

    cfCities.forEach(city => {
      const coords = cityCoords[city.name];
      if (coords) { city.lat = coords.lat; city.lng = coords.lng; }
    });

    cfCities.forEach(city => cfAddCityCloud(city));
    cfBuildLeaderboard();
    cfBuildSelect();
    cfBuildAmounts();
    cfUpdatePerks();
    cfUpdateDonateBtn();
  }

  /* ── Peek menu (green cloud overlapping mural pins) ─────────────── */
  function cfShowPeekMenu(pinEl, city, nearbyMurals) {
    // Remove existing
    const existing = document.getElementById('cf-peek-menu');
    if (existing) existing.remove();

    const menu = document.createElement('div');
    menu.id = 'cf-peek-menu';
    menu.className = 'cf-peek-menu';

    const heading = document.createElement('p');
    heading.className = 'cf-peek-heading';
    heading.textContent = 'What would you like to view?';
    menu.appendChild(heading);

    // City funding option
    const cityBtn = document.createElement('button');
    cityBtn.className = 'cf-peek-item cf-peek-item--city';
    cityBtn.innerHTML = `
      <div class="cf-peek-icon">🎨</div>
      <div class="cf-peek-info">
        <span class="cf-peek-name">Fund ${city.name.split(',')[0]}</span>
        <span class="cf-peek-sub">Donate to bring Andaluz here</span>
      </div>`;
    cityBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.remove();
      cfTab('donate', document.querySelector('.cf-tab-btn[data-tab="donate"]'));
      const sel = document.getElementById('cf-city-select');
      if (sel) { sel.value = city.id; cfOnCityChange(); }
      const tabsBar = document.querySelector('.cf-tabs-bar');
      if (tabsBar) tabsBar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    menu.appendChild(cityBtn);

    // Nearby mural options
    nearbyMurals.forEach(({ mural }) => {
      const muralBtn = document.createElement('button');
      muralBtn.className = 'cf-peek-item cf-peek-item--mural';
      const cleanName = mural.name ? mural.name.replace(/\(gone\)/gi, '').trim() : 'Untitled';
      muralBtn.innerHTML = `
        <div class="cf-peek-icon">📍</div>
        <div class="cf-peek-info">
          <span class="cf-peek-name">${cleanName}</span>
          <span class="cf-peek-sub">${mural.city || ''} · View mural</span>
        </div>`;
      muralBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        menu.remove();
        openModal(mural);
      });
      menu.appendChild(muralBtn);
    });

    document.body.appendChild(menu);

    // Position above pin
    const rect   = pinEl.getBoundingClientRect();
    const mWidth = 240;
    let left = rect.left + rect.width / 2 - mWidth / 2;
    let top  = rect.top - menu.offsetHeight - 12;
    left = Math.max(8, Math.min(left, window.innerWidth - mWidth - 8));
    if (top < 8) top = rect.bottom + 12;

    menu.style.left  = left + 'px';
    menu.style.top   = top  + 'px';
    menu.style.width = mWidth + 'px';

    requestAnimationFrame(() => {
      let t = rect.top - menu.offsetHeight - 12;
      if (t < 8) t = rect.bottom + 12;
      t = Math.max(8, Math.min(t, window.innerHeight - menu.offsetHeight - 8));
      menu.style.top = t + 'px';
    });

    setTimeout(() => {
      document.addEventListener('click', () => menu.remove(), { once: true });
    }, 0);
  }

    /* ── Bootstrap ───────────────────────────────────────────────────── */
  function bootstrap() {
    const apiKey = window.MURAL_MAP_API_KEY;
    if (!apiKey) {
      const canvas = document.getElementById('mural-map-canvas');
      if (canvas) canvas.textContent = 'Map unavailable: add your Google Maps API key in the section settings.';
      return;
    }
    window.initMuralMap = initMap;
    const script = document.createElement('script');
    script.src   = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMuralMap&loading=async`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

})();